export const insightArticles = [
  {
    slug: 'why-strategies-fail-in-the-middle',
    category: 'Strategy',
    date: 'September 2026',
    title: 'Why most strategies fail in the middle',
    summary: 'Strategies rarely break at the top or at the frontline. They stall in the layer of managers asked to translate them.',
    intro: 'A strategy is only as clear as its least-informed owner.',
    sections: [
      {
        heading: 'The handover problem',
        paragraphs: [
          'Executive teams often spend months debating a direction, then hand it to the next layer of leadership in a single presentation. The reasoning, the rejected options and the trade-offs stay in the room where they were made.',
          'What arrives in the middle of the organization is a conclusion without its logic. Managers are asked to act on it while still running the business that existed before the strategy was written.',
        ],
      },
      {
        heading: 'Translation is real work',
        paragraphs: [
          'Turning a direction into team priorities requires judgement. Which work stops? Which customers matter less? Which measures change? These are not administrative questions, and they rarely have obvious answers.',
          'When that work is not acknowledged, people fill the gap with local interpretation. Each team does something reasonable, and together they drift.',
        ],
      },
      {
        heading: 'Giving the middle a seat',
        paragraphs: [
          'Organizations that execute well tend to share context, not just conclusions. They invite managers into the reasoning early, give them time to test it against reality and give them explicit permission to make trade-offs.',
          'The aim is not more communication. It is fewer, better conversations about what the strategy means for the decisions people will make on Monday.',
        ],
      },
    ],
    quote: 'Each team does something reasonable, and together they drift.',
    cover: 'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_ins01.webp',
    gallery: [
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i1a.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i1b.webp',
    ],
  },
  {
    slug: 'operating-models-are-decisions',
    category: 'Transformation',
    date: 'July 2026',
    title: 'Operating models are decisions, not diagrams',
    summary: 'An org chart describes reporting lines. An operating model describes who decides what, and how fast.',
    intro: 'Friction rarely lives in the boxes. It lives in the decisions between them.',
    sections: [
      {
        heading: 'Why diagrams win attention',
        paragraphs: [
          'Redesign efforts often begin with structure because structure is visible. Boxes and lines can be drawn, debated and announced. They feel like progress.',
          'Yet many of the frustrations people describe have little to do with reporting lines. They describe waiting for approvals, repeating the same discussion in three forums, or not knowing who can say yes.',
        ],
      },
      {
        heading: 'Start with the decisions',
        paragraphs: [
          'A more useful starting point is a short list of the decisions that matter most to the strategy: pricing, investment, hiring, product priorities. For each, ask who proposes, who decides, who must be consulted and how quickly it should happen.',
          'This conversation surfaces overlap and ambiguity faster than any organizational chart. It also tends to be more honest.',
        ],
      },
      {
        heading: 'Let structure follow',
        paragraphs: [
          'Once decision rights are clear, structural choices become easier to judge. A reporting line either supports a decision flow or gets in its way.',
          'Governance can be simplified in the same spirit. Fewer forums, each with a clear purpose, usually serve an organization better than a dense calendar of reviews.',
        ],
      },
    ],
    quote: 'A reporting line either supports a decision flow or gets in its way.',
    cover: 'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_ins02.webp',
    gallery: [
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i2a.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i2b.webp',
    ],
  },
  {
    slug: 'cost-of-a-misaligned-top-team',
    category: 'Leadership',
    date: 'May 2026',
    title: 'The quiet cost of a misaligned top team',
    summary: 'Leadership misalignment is rarely loud. It shows up as slow decisions and polite meetings.',
    intro: 'Alignment is a practice, not a workshop.',
    sections: [
      {
        heading: 'Agreement in the room',
        paragraphs: [
          'Most executive teams do not argue openly. Meetings end with apparent agreement, actions are noted, and everyone returns to their own part of the organization.',
          'The disagreement surfaces later, in how resources are allocated, which projects move quickly and which messages reach teams. People below the top team notice long before the leaders themselves do.',
        ],
      },
      {
        heading: 'What alignment is not',
        paragraphs: [
          'Alignment does not mean consensus on everything. Capable leaders will see situations differently, and that tension is useful when it is expressed.',
          'It means a shared understanding of the few choices that matter most, and a commitment to defend them together once they are made, especially when they are uncomfortable.',
        ],
      },
      {
        heading: 'Making it a habit',
        paragraphs: [
          'A single offsite rarely holds. Alignment tends to last when it is revisited regularly: a short, honest review of where priorities are being interpreted differently and why.',
          'Teams that do this well name the trade-offs explicitly, return to them when circumstances change and treat a visible disagreement as information rather than failure.',
        ],
      },
    ],
    quote: 'People below the top team notice long before the leaders themselves do.',
    cover: 'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_ins03.webp',
    gallery: [
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i3a.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i3b.webp',
    ],
  },
  {
    slug: 'beyond-the-annual-plan',
    category: 'Strategy',
    date: 'March 2026',
    title: 'Beyond the annual plan',
    summary: 'A yearly plan sets a budget. It rarely tells a business how to respond when the year changes.',
    intro: 'The plan is a starting position, not a forecast to defend.',
    sections: [
      {
        heading: 'A ritual with limits',
        paragraphs: [
          'The annual planning cycle brings useful discipline. Targets are set, budgets are agreed and teams know what they are accountable for.',
          'Its weakness is timing. Plans are written months before they take effect, and by the second quarter the assumptions behind them may already look dated. Teams then spend energy explaining variance instead of responding to it.',
        ],
      },
      {
        heading: 'Separating direction from allocation',
        paragraphs: [
          'One way forward is to hold direction steady while making allocation more flexible. The few long-term choices about where the business competes change slowly. The resources behind them can move more often.',
          'This requires leaders to agree in advance which signals would prompt a shift, so that reallocating money or people is a planned response rather than a crisis.',
        ],
      },
      {
        heading: 'Shorter, sharper reviews',
        paragraphs: [
          'Quarterly conversations focused on a handful of questions tend to work better than long reviews of every line. What has changed? Which assumptions no longer hold? What should we stop?',
          'The aim is not constant replanning. It is a business that can adjust deliberately without losing sight of where it is going.',
        ],
      },
    ],
    quote: 'Hold direction steady. Let allocation move.',
    cover: 'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_ins04.webp',
    gallery: [
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i4a.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i4b.webp',
    ],
  },
  {
    slug: 'focus-in-uncertain-markets',
    category: 'Perspective',
    date: 'January 2026',
    title: 'What focus looks like in uncertain markets',
    summary: 'When the outlook is unclear, the instinct is to keep every option open. Focus asks for the opposite.',
    intro: 'Uncertainty is a reason to choose, not a reason to wait.',
    sections: [
      {
        heading: 'The cost of keeping options open',
        paragraphs: [
          'In volatile conditions, many leadership teams hedge. They fund several directions at once, delay commitments and wait for the picture to clear.',
          'Each hedge seems prudent on its own. Together they spread attention and capital thinly, and the organization ends up moving slowly in many directions rather than decisively in one.',
        ],
      },
      {
        heading: 'Choosing what not to do',
        paragraphs: [
          'Focus is often described as picking priorities. In practice, it is more about deciding what to stop, pause or decline, and saying so clearly.',
          'Those decisions are uncomfortable because they close doors. But they also free people to commit to the work that remains.',
        ],
      },
      {
        heading: 'Focus with room to adapt',
        paragraphs: [
          'Focus does not have to mean rigidity. A business can commit firmly to a few bets while agreeing on the conditions that would make it revisit them.',
          'That combination lets teams act with conviction today and gives leaders a disciplined way to change course if the market moves.',
        ],
      },
    ],
    quote: 'Focus is less about what to do than about what to stop.',
    cover: 'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_ins05.webp',
    gallery: [
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i5a.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_i5b.webp',
    ],
  },
];